import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useEditorState, type Editor } from '@tiptap/react'
import { Icon } from '../../ui/Icon'
import { Select, type SelectOption } from '../../ui/Select'
import { HIGHLIGHTS, TEXT_COLORS, imageSrc, linkHref, sameColor, type Swatch } from './editor'

/** Значок кнопки панели: линии в сетке 24, цвет — от кнопки. */
function Glyph({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  )
}

interface Tool {
  name: string
  glyph: ReactNode
  run: (editor: Editor) => void
  /** Переключатель: включённое форматирование видно по `aria-pressed`. */
  active?: (editor: Editor) => boolean
  /** Когда действие недоступно — кнопка выключена. */
  enabled?: (editor: Editor) => boolean
}

const MARKS: Tool[] = [
  {
    name: 'Жирный',
    glyph: <b>Ж</b>,
    run: (e) => e.chain().focus().toggleBold().run(),
    active: (e) => e.isActive('bold'),
  },
  {
    name: 'Курсив',
    glyph: <i>К</i>,
    run: (e) => e.chain().focus().toggleItalic().run(),
    active: (e) => e.isActive('italic'),
  },
  {
    name: 'Подчёркнутый',
    glyph: <u>Ч</u>,
    run: (e) => e.chain().focus().toggleUnderline().run(),
    active: (e) => e.isActive('underline'),
  },
  {
    name: 'Зачёркнутый',
    glyph: <s>З</s>,
    run: (e) => e.chain().focus().toggleStrike().run(),
    active: (e) => e.isActive('strike'),
  },
]

/** Выравнивание «по левому краю» — это отсутствие выравнивания: HTML чище. */
const SET_ALIGNS = ['center', 'right', 'justify']

const ALIGNS: Tool[] = [
  {
    name: 'По левому краю',
    glyph: <Glyph d="M3 6h18M3 12h12M3 18h16" />,
    run: (e) => e.chain().focus().unsetTextAlign().run(),
    active: (e) => !SET_ALIGNS.some((align) => e.isActive({ textAlign: align })),
  },
  ...(
    [
      ['По центру', 'center', 'M3 6h18M6 12h12M4 18h16'],
      ['По правому краю', 'right', 'M3 6h18M9 12h12M5 18h16'],
      ['По ширине', 'justify', 'M3 6h18M3 12h18M3 18h18'],
    ] as const
  ).map(([name, align, d]): Tool => ({
    name,
    glyph: <Glyph d={d} />,
    run: (e) => e.chain().focus().setTextAlign(align).run(),
    active: (e) => e.isActive({ textAlign: align }),
  })),
]

const BLOCKS: Tool[] = [
  {
    name: 'Маркированный список',
    glyph: <Glyph d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />,
    run: (e) => e.chain().focus().toggleBulletList().run(),
    active: (e) => e.isActive('bulletList'),
  },
  {
    name: 'Нумерованный список',
    glyph: <Glyph d="M10 6h10M10 12h10M10 18h10M4 4.5h1.5V9M3.5 14.5h2.5L3.5 19H6" />,
    run: (e) => e.chain().focus().toggleOrderedList().run(),
    active: (e) => e.isActive('orderedList'),
  },
  {
    name: 'Цитата',
    glyph: <Glyph d="M4 5v14M9 8h11M9 12h11M9 16h7" />,
    run: (e) => e.chain().focus().toggleBlockquote().run(),
    active: (e) => e.isActive('blockquote'),
  },
  {
    name: 'Разделитель',
    glyph: <Glyph d="M4 12h16M8 6h8M8 18h8" />,
    run: (e) => e.chain().focus().setHorizontalRule().run(),
  },
]

const TABLE: Tool = {
  name: 'Таблица',
  glyph: <Glyph d="M3 5h18v14H3zM3 10h18M3 15h18M9 5v14M15 5v14" />,
  run: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
  // Таблица в таблице на телефоне не читается.
  enabled: (e) => !e.isActive('table'),
}

const CLEAR: Tool = {
  name: 'Очистить форматирование',
  glyph: <Glyph d="M4 7V4h16v3M5 20h6M13 4 8 20M15 15l5 5M20 15l-5 5" />,
  run: (e) => e.chain().focus().unsetAllMarks().clearNodes().unsetTextAlign().run(),
}

const HISTORY: Tool[] = [
  {
    name: 'Отменить правку',
    glyph: <Glyph d="M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />,
    run: (e) => e.chain().focus().undo().run(),
    enabled: (e) => e.can().undo(),
  },
  {
    name: 'Повторить правку',
    glyph: <Glyph d="m15 14 5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />,
    run: (e) => e.chain().focus().redo().run(),
    enabled: (e) => e.can().redo(),
  },
]

const TOOLS = [...MARKS, ...ALIGNS, ...BLOCKS, TABLE, CLEAR, ...HISTORY]

const TABLE_ACTIONS: [string, (editor: Editor) => void][] = [
  ['Строка ниже', (e) => e.chain().focus().addRowAfter().run()],
  ['Удалить строку', (e) => e.chain().focus().deleteRow().run()],
  ['Столбец справа', (e) => e.chain().focus().addColumnAfter().run()],
  ['Удалить столбец', (e) => e.chain().focus().deleteColumn().run()],
  ['Удалить таблицу', (e) => e.chain().focus().deleteTable().run()],
]

type Block = 'p' | 'h1' | 'h2' | 'h3'

const BLOCK_OPTIONS: SelectOption<Block>[] = [
  { value: 'p', label: 'Обычный текст' },
  { value: 'h1', label: 'Заголовок 1' },
  { value: 'h2', label: 'Заголовок 2' },
  { value: 'h3', label: 'Заголовок 3' },
]

const LEVELS = [1, 2, 3] as const

function setBlock(editor: Editor, block: Block) {
  const level = LEVELS.find((l) => `h${l}` === block)
  const chain = editor.chain().focus()
  if (level) chain.setHeading({ level }).run()
  else chain.setParagraph().run()
}

function ToolButton({
  name,
  glyph,
  pressed,
  disabled,
  expanded,
  onClick,
}: {
  name: string
  glyph: ReactNode
  pressed?: boolean
  disabled?: boolean
  expanded?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="ui-iconbtn"
      aria-label={name}
      data-tip={name}
      data-tip-pos="bottom"
      aria-pressed={pressed}
      aria-expanded={expanded}
      disabled={disabled}
      onClick={onClick}
    >
      {glyph}
    </button>
  )
}

/**
 * Цвет текста или фона: кнопка открывает несколько образцов и «Сбросить».
 * Закрывается выбором, Escape и нажатием мимо.
 */
function SwatchMenu({
  name,
  glyph,
  swatches,
  current,
  onPick,
}: {
  name: string
  glyph: ReactNode
  swatches: Swatch[]
  current: string | undefined
  onPick: (value: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => {
      if (!(box.current as HTMLDivElement).contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function pick(value: string | null) {
    onPick(value)
    setOpen(false)
  }

  return (
    <div className="st-rte-pop" ref={box}>
      <button
        type="button"
        className="ui-iconbtn"
        aria-label={name}
        data-tip={name}
        data-tip-pos="bottom"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open)
        }}
      >
        {glyph}
        {/* Полоска под значком — текущий цвет, как в текстовых редакторах. */}
        <span className="st-rte-current" style={{ background: current }} aria-hidden="true" />
      </button>
      {open ? (
        <div className="st-rte-menu" role="group" aria-label={name}>
          {swatches.map((swatch) => (
            <button
              key={swatch.value}
              type="button"
              className="ui-iconbtn st-swatch"
              aria-label={swatch.name}
              data-tip={swatch.name}
              aria-pressed={sameColor(current, swatch.value)}
              onClick={() => {
                pick(swatch.value)
              }}
            >
              <span style={{ background: swatch.value }} aria-hidden="true" />
            </button>
          ))}
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            data-size="sm"
            onClick={() => {
              pick(null)
            }}
          >
            Сбросить
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Строка под панелью: адрес ссылки или картинки. */
function UrlRow({
  label,
  value,
  error,
  applyLabel,
  onChange,
  onApply,
  onClose,
  children,
}: {
  label: string
  value: string
  error?: string
  applyLabel: string
  onChange: (value: string) => void
  onApply: () => void
  onClose: () => void
  children?: ReactNode
}) {
  return (
    <div className="st-rte-row">
      <input
        className="ui-input"
        data-size="sm"
        type="url"
        inputMode="url"
        aria-label={label}
        placeholder="https://"
        // Строка открывается по нажатию кнопки — фокус сразу в поле.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'st-rte-url-error' : undefined}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onApply()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
          }
        }}
      />
      <button type="button" className="ui-btn" data-size="sm" onClick={onApply}>
        {applyLabel}
      </button>
      {children}
      <button
        type="button"
        className="ui-btn"
        data-variant="ghost"
        data-size="sm"
        onClick={onClose}
      >
        Отмена
      </button>
      {error ? (
        <span className="ui-error" id="st-rte-url-error" role="alert">
          <Icon name="i-alert" />
          {error}
        </span>
      ) : null}
    </div>
  )
}

interface Panel {
  kind: 'link' | 'image'
  value: string
  error?: string
}

/**
 * Панель форматирования. Прилипает к верху редактора: длинный текст
 * листают, а кнопки остаются под рукой. На узком экране переносится в
 * несколько строк. Сочетания клавиш (Ctrl/Cmd+B, I, U, Z) дают сами
 * расширения редактора.
 */
export function RichTextToolbar({ editor }: { editor: Editor }) {
  const [panel, setPanel] = useState<Panel | null>(null)
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const level = LEVELS.find((l) => e.isActive('heading', { level: l }))
      return {
        active: Object.fromEntries(
          TOOLS.map((tool) => [tool.name, tool.active ? tool.active(e) : undefined]),
        ),
        enabled: Object.fromEntries(
          TOOLS.map((tool) => [tool.name, tool.enabled ? tool.enabled(e) : true]),
        ),
        block: level ? (`h${level}` as const) : ('p' as const),
        color: (e.getAttributes('textStyle') as { color?: string }).color,
        highlight: (e.getAttributes('highlight') as { color?: string }).color,
        link: e.isActive('link'),
        table: e.isActive('table'),
      }
    },
  })

  const button = (tool: Tool) => (
    <ToolButton
      key={tool.name}
      name={tool.name}
      glyph={tool.glyph}
      pressed={state.active[tool.name]}
      disabled={!state.enabled[tool.name]}
      onClick={() => {
        tool.run(editor)
      }}
    />
  )

  function applyLink(raw: string) {
    const href = linkHref(raw)
    const chain = editor.chain().focus().extendMarkRange('link')
    if (!href) chain.unsetLink().run()
    else if (editor.state.selection.empty && !editor.isActive('link')) {
      // Ничего не выделено — вставляем сам адрес ссылкой, иначе нажатие
      // ничего бы не сделало.
      chain
        .insertContent({
          type: 'text',
          text: raw.trim(),
          marks: [{ type: 'link', attrs: { href } }],
        })
        .run()
    } else chain.setLink({ href }).run()
    setPanel(null)
  }

  function applyImage(raw: string) {
    const src = imageSrc(raw)
    if (!src) {
      setPanel({ kind: 'image', value: raw, error: 'Нужен адрес https://' })
      return
    }
    editor.chain().focus().setImage({ src }).run()
    setPanel(null)
  }

  return (
    <div className="st-rte-top">
      <div className="st-rte-bar" role="toolbar" aria-label="Форматирование">
        <div className="st-rte-group">
          <Select
            label="Стиль текста"
            size="sm"
            value={state.block}
            options={BLOCK_OPTIONS}
            onChange={(block) => {
              setBlock(editor, block)
            }}
          />
        </div>
        <div className="st-rte-group">
          {MARKS.map(button)}
          <SwatchMenu
            name="Цвет текста"
            glyph={<b>А</b>}
            swatches={TEXT_COLORS}
            current={state.color}
            onPick={(color) => {
              const chain = editor.chain().focus()
              if (color) chain.setColor(color).run()
              else chain.unsetColor().run()
            }}
          />
          <SwatchMenu
            name="Выделение"
            glyph={
              <Glyph d="m9 11-6 6v3h9l3-3M22 12l-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4" />
            }
            swatches={HIGHLIGHTS}
            current={state.highlight}
            onPick={(color) => {
              const chain = editor.chain().focus()
              if (color) chain.setHighlight({ color }).run()
              else chain.unsetHighlight().run()
            }}
          />
        </div>
        <div className="st-rte-group">{ALIGNS.map(button)}</div>
        <div className="st-rte-group">{BLOCKS.map(button)}</div>
        <div className="st-rte-group">
          <ToolButton
            name="Ссылка"
            glyph={
              <Glyph d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
            }
            pressed={state.link}
            expanded={panel?.kind === 'link'}
            onClick={() => {
              const { href } = editor.getAttributes('link') as { href?: string }
              setPanel({ kind: 'link', value: href ?? '' })
            }}
          />
          <ToolButton
            name="Картинка"
            glyph={<Icon name="i-img" />}
            expanded={panel?.kind === 'image'}
            onClick={() => {
              setPanel({ kind: 'image', value: '' })
            }}
          />
          {button(TABLE)}
        </div>
        <div className="st-rte-group">
          {button(CLEAR)}
          {HISTORY.map(button)}
        </div>
      </div>

      {panel?.kind === 'link' ? (
        <UrlRow
          label="Адрес ссылки"
          value={panel.value}
          applyLabel="Применить"
          onChange={(value) => {
            setPanel({ kind: 'link', value })
          }}
          onApply={() => {
            applyLink(panel.value)
          }}
          onClose={() => {
            setPanel(null)
          }}
        >
          {state.link ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => {
                applyLink('')
              }}
            >
              Убрать ссылку
            </button>
          ) : null}
        </UrlRow>
      ) : null}

      {panel?.kind === 'image' ? (
        <UrlRow
          label="Адрес картинки"
          value={panel.value}
          error={panel.error}
          applyLabel="Вставить"
          onChange={(value) => {
            setPanel({ kind: 'image', value })
          }}
          onApply={() => {
            applyImage(panel.value)
          }}
          onClose={() => {
            setPanel(null)
          }}
        />
      ) : null}

      {/* Действия с таблицей — только когда курсор в ней. */}
      {state.table ? (
        <div className="st-rte-row" role="group" aria-label="Таблица">
          {TABLE_ACTIONS.map(([label, run]) => (
            <button
              key={label}
              type="button"
              className="ui-btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => {
                run(editor)
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
